// Trang chủ 3 cột: nhóm người dùng, thông báo công ty, chỉ tiêu + tín hiệu cần xử lý tính từ thẻ liệu trình (UI-first)
import dayjs from 'dayjs';
import type { PayRequest, TreatmentCard, TreatmentConfig } from '../../Treatment/data';
import { cardFlags } from '../../Treatment/logic';

export type HomeRole = 'sale' | 'doctor' | 'telesale' | 'manager';

export const ROLE_META: Record<HomeRole, { label: string; short: string; icon: string; color: string }> = {
  sale: { label: 'Sale', short: 'Sale', icon: 'fa-solid fa-hand-holding-dollar', color: '#f59e0b' },
  doctor: { label: 'Bác sĩ / KTV', short: 'Bác sĩ', icon: 'fa-solid fa-user-doctor', color: '#14b8a6' },
  telesale: { label: 'Telesale / CSKH', short: 'Telesale', icon: 'fa-solid fa-headset', color: '#3b82f6' },
  manager: { label: 'Quản lý', short: 'Quản lý', icon: 'fa-solid fa-user-tie', color: '#a855f7' },
};
export const HOME_ROLES = Object.keys(ROLE_META) as HomeRole[];

const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();

/** Đoán nhóm theo tên vai trò (cấu hình tay ở roleMap được ưu tiên). Telesale xét trước Sale vì tên chứa "sale". */
export function detectRole(roleName: string, isSuperAdmin: boolean, roleMap: Record<string, HomeRole> = {}): HomeRole {
  if (roleName && roleMap[roleName]) return roleMap[roleName];
  const n = normalize(roleName || '');
  if (/tele|cskh|cham soc|call/.test(n)) return 'telesale';
  if (/bac si|doctor|ktv|ky thuat|dieu duong|y ta/.test(n)) return 'doctor';
  if (/sale|tu van|kinh doanh/.test(n)) return 'sale';
  if (isSuperAdmin || /quan ly|manager|giam doc|truong|admin|leader/.test(n)) return 'manager';
  return 'sale';
}

// ---------------------------------------------------------------------------
// Chỉ tiêu tháng

export interface RoleTarget { value: number; unit: string; label: string }

export const DEFAULT_TARGETS: Record<HomeRole, RoleTarget> = {
  sale: { value: 400_000_000, unit: 'đ', label: 'Doanh số cá nhân' },
  doctor: { value: 80, unit: 'ca', label: 'Ca thực hiện' },
  telesale: { value: 120, unit: 'lịch', label: 'Lịch hẹn đặt được' },
  manager: { value: 3_000_000_000, unit: 'đ', label: 'Doanh số cơ sở' },
};

// ---------------------------------------------------------------------------
// Thông báo công ty

export type AnnLevel = 'urgent' | 'policy' | 'event' | 'info';

export const ANN_LEVEL: Record<AnnLevel, { label: string; cls: string }> = {
  urgent: { label: 'Khẩn', cls: 'bg-red-500/20 text-red-300 border-red-400/40' },
  policy: { label: 'Quy định', cls: 'bg-amber-500/20 text-amber-200 border-amber-400/40' },
  event: { label: 'Sự kiện', cls: 'bg-sky-500/20 text-sky-200 border-sky-400/40' },
  info: { label: 'Tin chung', cls: 'bg-slate-500/20 text-slate-200 border-slate-400/40' },
};

export interface Announcement {
  id: string;
  title: string;
  content: string;
  level: AnnLevel;
  pinned: boolean;
  /** Bắt buộc bấm "Đã đọc và hiểu" (quy trình, chính sách) */
  requireAck: boolean;
  /** Nhóm nhận; rỗng = toàn công ty */
  audience: HomeRole[];
  /** Hiển thị đến hết ngày (YYYY-MM-DD); rỗng = không hết hạn */
  until: string;
  createdAt: string;
  by: string;
  demo?: boolean;
}

/** Thông báo còn hiệu lực cho nhóm: ghim lên đầu, rồi mới nhất */
export function visibleAnnouncements(list: Announcement[], role: HomeRole, today = dayjs().format('YYYY-MM-DD')) {
  return list
    .filter(a => (!a.audience.length || a.audience.includes(role)) && (!a.until || a.until >= today))
    .sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.createdAt.localeCompare(a.createdAt));
}

// ---------------------------------------------------------------------------
// Tín hiệu từ thẻ liệu trình (lưu trên máy)

export interface CardSignals {
  overdue: number;
  overdueAmount: number;
  dueSoon: number;
  expiring: number;
  low: number;
  pendingRefund: number;
  pendingPay: number;
  /** Có thẻ còn hiệu lực nhưng không quay lại quá N ngày */
  idle: number;
  usagesToday: number;
  bookingsToday: number;
}

export function cardSignals(cards: TreatmentCard[], payRequests: PayRequest[], cfg: TreatmentConfig, idleDays = 45, now = new Date()): CardSignals {
  const today = dayjs(now).format('YYYY-MM-DD');
  const idleSince = dayjs(now).subtract(idleDays, 'day').toISOString();
  const s: CardSignals = { overdue: 0, overdueAmount: 0, dueSoon: 0, expiring: 0, low: 0, pendingRefund: 0, pendingPay: 0, idle: 0, usagesToday: 0, bookingsToday: 0 };
  const idleCustomers = new Set<string>();
  const activeCustomers = new Set<string>();
  for (const c of cards) {
    const f = cardFlags(c, cfg, now);
    if (f.overdue) { s.overdue++; s.overdueAmount += f.overdueAmount; }
    if (f.dueSoon) s.dueSoon++;
    if (f.expiring) s.expiring++;
    if (f.low) s.low++;
    if (f.pendingRefund) s.pendingRefund++;
    const used = c.usages.filter(u => !u.cancelled);
    s.usagesToday += used.filter(u => u.at.slice(0, 10) === today).length;
    s.bookingsToday += c.bookings.filter(b => b.status === 'booked' && b.at.slice(0, 10) === today).length;
    if (f.status === 'active') {
      const last = used.reduce((m, u) => (u.at > m ? u.at : m), c.createdAt);
      if (last >= idleSince) activeCustomers.add(c.customerId);
      else idleCustomers.add(c.customerId);
    }
  }
  s.idle = Array.from(idleCustomers).filter(id => !activeCustomers.has(id)).length;
  s.pendingPay = payRequests.filter(r => r.status === 'pending').length;
  return s;
}

export const moneyShort = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(a >= 1e10 ? 0 : 1).replace('.', ',')} tỷ`;
  if (a >= 1e6) return `${Math.round(n / 1e6)}tr`;
  if (a >= 1e3) return `${Math.round(n / 1e3)}k`;
  return String(Math.round(n));
};
